import { useState } from 'react';
import { EmailEditor } from '../../components/phase2';
import type { EmailDraft } from '../../components/phase2';
import type { MergeContext } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { uploadAttachment } from './api';
import { errorMessage } from '../../components/ErrorPanel';

/** The stored shape both the expected-visit and the onboarding emails share. */
export interface StoredEmail {
  subject: string;
  body: string;
  attachmentFileId: string | null;
  replyTo: string | null;
  includeQr: boolean;
  includeCalendar?: boolean;
}

/**
 * The W9 email editor wired to a stored email: maps the settings fields to the editor's draft and uploads the attachment
 * to the module's own route. "Reset to default" empties the subject and body, which the server reads as "use the built-in text".
 */
export function EmailSection<E extends StoredEmail>({
  value,
  onChange,
  context,
  uploadPath,
  groupId,
  showCalendar,
  disabled,
}: {
  value: E;
  onChange: (next: E) => void;
  context: MergeContext;
  uploadPath: string;
  groupId: string;
  showCalendar: boolean;
  disabled: boolean;
}) {
  const [file, setFile] = useState<{ name: string; size: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const attachment = value.attachmentFileId ? (file ?? { name: t('prereg.email.storedAttachment'), size: 0 }) : null;
  const draft: EmailDraft = {
    subject: value.subject,
    body: value.body,
    replyTo: value.replyTo ?? '',
    attachment,
    qr: value.includeQr,
    calendar: value.includeCalendar ?? false,
  };
  const apply = (d: EmailDraft) => {
    const next = { ...value, subject: d.subject, body: d.body, replyTo: d.replyTo.trim() === '' ? null : d.replyTo, includeQr: d.qr };
    if (showCalendar) next.includeCalendar = d.calendar;
    if (d.attachment === null && value.attachmentFileId !== null) {
      next.attachmentFileId = null;
      setFile(null);
    }
    onChange(next);
  };
  return (
    <div>
      <EmailEditor
        value={draft}
        onChange={apply}
        context={context}
        showCalendar={showCalendar}
        disabled={disabled}
        onReset={() => onChange({ ...value, subject: '', body: '' })}
        onAttachmentChosen={(f) => {
          setUploadError(null);
          uploadAttachment(uploadPath, groupId, f)
            .then((fileId) => {
              setFile({ name: f.name, size: f.size });
              onChange({ ...value, attachmentFileId: fileId });
            })
            .catch((e: unknown) => setUploadError(errorMessage(e)));
        }}
      />
      {uploadError ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {uploadError}
        </p>
      ) : null}
    </div>
  );
}
